/* eslint-disable react/prop-types */
/* eslint-disable react/display-name */
import React, { useState, useRef, useEffect, useLayoutEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import LibSelect, { Props as SelectProps, GroupTypeBase, Styles } from 'react-select';
import colors from './../../../styles/export/colors.module.scss';
import styles from './Select.module.scss';
import { FaChevronDown, FaTimes, FaCheck } from 'react-icons/fa';
import { ReactComponent as AddIcon } from 'assets/svg/desktop_add_new2.svg';
import ReactTooltip from 'react-tooltip';
import { SelectValueLabel } from 'common/types/SelectValueLabel';

// Declaração de tipo para ResizeObserver (não está no lib dom desta versão do TS)
declare class ResizeObserver {
    constructor(callback: () => void);
    observe(target: Element): void;
    disconnect(): void;
}

export type Props = {
    isDisabled?: boolean;
    isSimple?: boolean;
    removeInputBorderRadius?: boolean;
    minWidth?: string;
    showMultiOptions?: boolean;
    isCreatable?: boolean;
    onCreateOption?: (name: string) => void;
    createOptionText?: string;
    defaultMenuIsOpen?: boolean;
    slim?: boolean;
    usePortal?: boolean;
    growScrollOnOpen?: boolean;
    // Task #66347: sem moldura nem cantos, para caber numa célula da grelha.
    flat?: boolean;
    // Task #66347: fechado, mostra só as primeiras N etiquetas e um "+N" com o resto — numa célula
    // de grelha a lista completa não cabe e não se lê nada. Aberto, mostra sempre tudo.
    maxVisibleTags?: number;
    // Task #66347: avisa quem usa o select de que o menu abriu/fechou — quem vive numa grelha que
    // remonta a célula precisa de saber para o reabrir (ver defaultMenuIsOpen).
    onOpenChange?: (isOpen: boolean) => void;
    styles?: Partial<Styles<{ label: string; value: string; }, false, GroupTypeBase<{ label: string; value: string; }>>>;
} & SelectProps;

const Select = React.forwardRef<LibSelect, Props>(({ minWidth, showMultiOptions, isCreatable, onCreateOption, createOptionText, defaultMenuIsOpen, slim, usePortal, growScrollOnOpen, flat, maxVisibleTags, onOpenChange, ...props }, ref) => {
    const { t } = useTranslation();

    // Multi-select custom rendering
    if (props.isMulti) {
        return (
            <SelectMultiCustom
                {...props}
                showMultiOptions={showMultiOptions}
                isCreatable={isCreatable}
                onCreateOption={onCreateOption}
                createOptionText={createOptionText}
                defaultMenuIsOpen={defaultMenuIsOpen}
                slim={slim}
                flat={flat}
                maxVisibleTags={maxVisibleTags}
                onOpenChange={onOpenChange}
                usePortal={usePortal}
                growScrollOnOpen={growScrollOnOpen}
            />
        );
    }

    // Original single select with react-select
    return (
        <LibSelect
            {...props}
            ref={ref}
            noOptionsMessage={() => t('common.no_options')}
            menuPlacement="auto"
            menuPortalTarget={usePortal ? document.body : undefined}
            menuPosition={usePortal ? 'fixed' : undefined}
            styles={{
                control: (base, state) => ({
                    ...base,
                    backgroundColor: props.isDisabled ? colors.whisper : colors.white,
                    borderRadius: props.removeInputBorderRadius ? '20px 0px 0 20px !important' : '20px !important',
                    border: props.isSimple ? '0px' : state.isFocused ? `1px solid ${colors.success}` : `1px solid ${colors.mercury}`,
                    boxShadow: 'none',
                    ':hover': {
                        borderColor: state.isFocused ? colors.success : colors.mercury,
                    },
                    minWidth: minWidth,
                    zIndex: 0
                }),
                menu: base => ({
                    ...base,
                    zIndex: 999
                }),
                menuPortal: base => ({
                    ...base,
                    zIndex: 1100
                }),
                placeholder: base => ({
                    ...base,
                    color: colors.frenchGray,
                    opacity: 0.54,
                }),
                valueContainer: base => ({
                    ...base,
                    padding: props.isSimple ? '5.75px 0.5rem' : '5.75px 1rem',
                    justifyContent: props.isSimple ? 'flex-end' : 'initial',
                    zIndex: 0
                }),
                option: (base, state) => ({
                    ...base,
                    color: state.isSelected ? colors.white : colors.black,
                    // Task #66347: `isFocused` é a opção onde as setas do teclado estão. Sem isto só o
                    // rato dava pista visual e navegar com o teclado era às cegas até carregar Enter.
                    backgroundColor: state.isSelected
                        ? colors.primaryDark
                        : state.isFocused ? colors.lightGrayishBlue : colors.white,
                    ':hover': {
                        backgroundColor: colors.lightGrayishBlue,
                        color: colors.black
                    },
                }),
                singleValue: base => ({
                    ...base,
                    color: colors.lightblack,
                }),
                multiValueLabel: base => ({
                    ...base,
                    backgroundColor: colors.solitude,
                    color: colors.black,
                }),
                multiValueRemove: base => ({
                    ...base,
                    backgroundColor: colors.solitude,
                    color: colors.black,
                }),
                indicatorSeparator: base => ({
                    ...base,
                    width: props.isSimple ? '0px' : '1px',
                    backgroundColor: colors.mercury
                }),
                ...props.styles
            }}
        />
    );
});

// Custom Multi Select Component
interface SelectMultiCustomProps {
    options?: readonly any[];
    value?: any;
    onChange?: (value: any, actionMeta: any) => void;
    placeholder?: React.ReactNode;
    isDisabled?: boolean;
    showMultiOptions?: boolean;
    isCreatable?: boolean;
    onCreateOption?: (name: string) => void;
    createOptionText?: string;
    isClearable?: boolean;
    filterOption?: ((option: any, inputValue: string) => boolean) | null;
    defaultMenuIsOpen?: boolean;
    slim?: boolean;
    flat?: boolean;
    maxVisibleTags?: number;
    onOpenChange?: (isOpen: boolean) => void;
    usePortal?: boolean;
    growScrollOnOpen?: boolean;
}

const SelectMultiCustom: React.FC<SelectMultiCustomProps> = ({
    options = [],
    value,
    onChange,
    placeholder,
    isDisabled = false,
    showMultiOptions = false,
    isCreatable = false,
    onCreateOption,
    createOptionText,
    isClearable = true,
    filterOption,
    defaultMenuIsOpen = false,
    slim = false,
    flat = false,
    maxVisibleTags,
    onOpenChange,
    usePortal = false,
    growScrollOnOpen = false
}) => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(defaultMenuIsOpen);
    const [searchTerm, setSearchTerm] = useState('');
    const dropdownRef = useRef<HTMLDivElement>(null);
    const portalDropdownRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const [portalStyle, setPortalStyle] = useState<React.CSSProperties>({});
    const [spacerHeight, setSpacerHeight] = useState(0);
    const tooltipId = useMemo(() => `select-tooltip-${Math.random().toString(36).substr(2, 9)}`, []);

    // Flatten grouped options to simple array
    const flatOptions: SelectValueLabel[] = (options as any[]).reduce((acc, opt) => {
        if ('options' in opt && Array.isArray(opt.options)) {
            return [...acc, ...opt.options];
        }
        return [...acc, opt];
    }, [] as SelectValueLabel[]);

    // Convert value to array
    const selectedValues: SelectValueLabel[] = Array.isArray(value) ? [...value] : (value ? [value] : []);

    // Task #66347: quantas etiquetas cabem na largura atual. Medem-se numa cópia invisível dentro da
    // própria caixa (herda os tamanhos do slim/flat) e recalcula-se sempre que a coluna muda de
    // largura — o "+N" tem de acompanhar, senão continua a esconder etiquetas que já cabiam.
    const tagsRef = useRef<HTMLDivElement>(null);
    const tagsMeasureRef = useRef<HTMLDivElement>(null);
    const [fitCount, setFitCount] = useState<number | null>(null);
    const selectedValuesKey = selectedValues.map(item => item.value).join('|');

    useLayoutEffect(() => {
        if (!maxVisibleTags || isOpen) return;
        const container = tagsRef.current;
        const mirror = tagsMeasureRef.current;
        if (!container || !mirror) return;

        const measure = () => {
            const nodes = Array.from(mirror.children) as HTMLElement[];
            // A última é sempre o "+N" — é o espaço que é preciso reservar quando sobra alguma.
            const moreWidth = nodes.length ? nodes[nodes.length - 1].offsetWidth : 0;
            const tags = nodes.slice(0, -1);
            const available = container.clientWidth;
            const gap = parseFloat(window.getComputedStyle(container).columnGap) || 0;

            let used = 0;
            let count = 0;
            for (let index = 0; index < tags.length; index++) {
                const next = used + (count > 0 ? gap : 0) + tags[index].offsetWidth;
                const reserve = index < tags.length - 1 ? gap + moreWidth : 0;
                if (next + reserve > available) break;
                used = next;
                count++;
            }

            setFitCount(count);
        };

        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(container);
        return () => observer.disconnect();
    }, [maxVisibleTags, isOpen, selectedValuesKey, isDisabled]);

    // Task #66347: quem monta o select numa grelha precisa de saber o estado do menu para o repor
    // depois de a célula remontar — a ref evita re-registar o efeito a cada render.
    const onOpenChangeRef = useRef(onOpenChange);
    onOpenChangeRef.current = onOpenChange;
    useEffect(() => {
        onOpenChangeRef.current?.(isOpen);
    }, [isOpen]);

    // Click outside handler — quando em modo portal, a dropdown está fora do
    // dropdownRef (foi montada em document.body), por isso testamos os dois.
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;
            const insideContainer = dropdownRef.current?.contains(target);
            const insidePortal = portalDropdownRef.current?.contains(target);
            if (!insideContainer && !insidePortal) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Em modo portal, posiciona a dropdown via fixed coords baseadas no selectBox.
    // Recalcula em scroll/resize para acompanhar transformações de layout.
    useEffect(() => {
        if (!usePortal || !isOpen) return;
        const update = () => {
            const box = dropdownRef.current?.getBoundingClientRect();
            if (!box) return;
            setPortalStyle({
                position: 'fixed',
                top: box.bottom + 4,
                left: box.left,
                width: box.width,
                zIndex: 1100,
            });
        };
        update();
        window.addEventListener('scroll', update, true);
        window.addEventListener('resize', update);
        return () => {
            window.removeEventListener('scroll', update, true);
            window.removeEventListener('resize', update);
        };
    }, [usePortal, isOpen]);

    // Em modo growScrollOnOpen (menus inline no fundo de formulários, ex.: gestão de
    // ativos do condutor), soma a altura da dropdown ao scroll da página através de um
    // spacer em fluxo — sem isto o menu (position: absolute) fica cortado abaixo da dobra.
    // Sem array de dependências de propósito: a altura do menu muda com a pesquisa e
    // o setState com o mesmo valor não re-renderiza (bail out do React).
    useLayoutEffect(() => {
        if (!growScrollOnOpen || usePortal) return;
        // 4px = margin-top do .dropdown
        setSpacerHeight(isOpen ? (portalDropdownRef.current?.offsetHeight ?? 0) + 4 : 0);
    });

    // Ao abrir, rola automaticamente até a dropdown ficar toda visível (o rAF garante
    // que o spacer já foi aplicado e há espaço de scroll para onde rolar).
    useEffect(() => {
        if (!growScrollOnOpen || usePortal || !isOpen) return;
        const raf = requestAnimationFrame(() => {
            portalDropdownRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        });
        return () => cancelAnimationFrame(raf);
    }, [growScrollOnOpen, usePortal, isOpen]);

    // Filter options based on search term
    const filteredOptions = flatOptions.filter(option => {
        if (filterOption) {
            return filterOption({ label: option.label, value: option.value, data: option }, searchTerm);
        }
        const search = searchTerm.toLowerCase();
        return option.label.toLowerCase().includes(search) ||
            (option.fullLabel ? option.fullLabel.toLowerCase().includes(search) : false);
    });

    // Check if option is selected
    const isSelected = (optionValue: string) => {
        return selectedValues.some(v => v.value === optionValue);
    };

    // Toggle option selection
    const toggleOption = (option: SelectValueLabel) => {
        if (isDisabled) return;

        const isCurrentlySelected = isSelected(option.value);
        let newValue: SelectValueLabel[];

        if (isCurrentlySelected) {
            newValue = selectedValues.filter(v => v.value !== option.value);
        } else {
            newValue = [...selectedValues, option];
        }

        onChange?.(newValue, { action: isCurrentlySelected ? 'deselect-option' : 'select-option' });
    };

    // Remove a specific option
    const removeOption = (optionValue: string, e: React.MouseEvent) => {
        e.stopPropagation();
        if (isDisabled) return;

        const newValue = selectedValues.filter(v => v.value !== optionValue);
        onChange?.(newValue, { action: 'remove-value' });
    };

    // Clear all selections
    const clearAll = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (isDisabled) return;

        onChange?.([], { action: 'clear' });
    };

    // Select all options
    const selectAll = () => {
        if (isDisabled) return;

        onChange?.(flatOptions, { action: 'select-option' });
    };

    // Handle creating new option
    const handleCreateOption = () => {
        if (!searchTerm.trim() || !onCreateOption) return;

        // Check if already exists
        const exists = flatOptions.some(
            o => o.label.toLowerCase() === searchTerm.toLowerCase()
        );
        if (exists) return;

        onCreateOption(searchTerm.trim());
        setSearchTerm('');
    };

    // Handle key down on input
    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' && searchTerm.trim()) {
            e.preventDefault();

            // Check if matches an existing option
            const existing = flatOptions.find(
                o => o.label.toLowerCase() === searchTerm.toLowerCase()
            );

            if (existing) {
                if (!isSelected(existing.value)) {
                    toggleOption(existing);
                }
                setSearchTerm('');
            } else if (isCreatable && onCreateOption) {
                handleCreateOption();
            }
        }
    };

    // Task #66347: fechado e com limite, só as etiquetas que cabem ficam à vista; o resto conta para
    // o "+N" (com a lista completa em tooltip). Aberto mostra tudo, porque aí há espaço.
    // O `maxVisibleTags` é o mínimo garantido — alargando a coluna da grelha entram mais etiquetas e
    // o "+N" encolhe, em vez de ficar preso ao número inicial.
    const visibleLimit = maxVisibleTags ? Math.max(maxVisibleTags, fitCount ?? maxVisibleTags) : undefined;
    const showAllTags = isOpen || !visibleLimit || selectedValues.length <= visibleLimit;
    const visibleValues = showAllTags ? selectedValues : selectedValues.slice(0, visibleLimit);
    const hiddenCount = selectedValues.length - visibleValues.length;

    // Check if search term can create new option
    const canCreateNew = isCreatable &&
        onCreateOption &&
        searchTerm.trim() &&
        !flatOptions.some(o => o.label.toLowerCase() === searchTerm.toLowerCase());

    return (
        <div className={`${styles.container} ${flat ? styles.containerFlat : ''}`} ref={dropdownRef}>
            <div
                className={`${styles.selectBox} ${isOpen ? styles.selectBoxOpen : ''} ${isDisabled ? styles.selectBoxDisabled : ''} ${slim ? styles.selectBoxSlim : ''} ${flat ? styles.selectBoxFlat : ''} ${maxVisibleTags && !isOpen ? styles.selectBoxSingleLine : ''}`}
                onClick={() => {
                    if (!isDisabled) {
                        setIsOpen(true);
                        inputRef.current?.focus();
                    }
                }}
            >
                <div className={styles.selectedTags} ref={tagsRef}>
                    {visibleValues.map(item => (
                        <span
                            key={item.value}
                            className={`${styles.selectedTag} ${isDisabled ? styles.selectedTagDisabled : ''}`}
                            data-tip={item.fullLabel || undefined}
                            data-for={item.fullLabel ? tooltipId : undefined}
                        >
                            {item.label}
                            {!isDisabled && (
                                <span
                                    className={styles.removeTag}
                                    onClick={e => removeOption(item.value, e)}
                                >
                                    <FaTimes size={10} />
                                </span>
                            )}
                        </span>
                    ))}

                    {hiddenCount > 0 && (
                        <span
                            className={styles.selectedTagMore}
                            data-tip={selectedValues.slice(visibleValues.length).map(item => item.label).join(', ')}
                            data-for={tooltipId}
                        >
                            +{hiddenCount}
                        </span>
                    )}

                    {!isDisabled && (isOpen || selectedValues.length === 0) && (
                        <input
                            ref={inputRef}
                            type="text"
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                            onKeyDown={handleKeyDown}
                            onFocus={() => setIsOpen(true)}
                            placeholder={selectedValues.length === 0 ? (typeof placeholder === 'string' ? placeholder : '') : ''}
                            className={styles.searchInput}
                        />
                    )}

                    {isDisabled && selectedValues.length === 0 && (
                        <span className={styles.placeholderText}>{placeholder || ''}</span>
                    )}
                </div>

                {!!maxVisibleTags && (
                    <div className={styles.tagsMeasure} ref={tagsMeasureRef} aria-hidden={true}>
                        {selectedValues.map(item => (
                            <span key={item.value} className={styles.selectedTag}>
                                {item.label}
                                {!isDisabled && (
                                    <span className={styles.removeTag}>
                                        <FaTimes size={10} />
                                    </span>
                                )}
                            </span>
                        ))}
                        <span className={styles.selectedTagMore}>+{selectedValues.length}</span>
                    </div>
                )}

                {!isDisabled && isClearable && selectedValues.length > 0 && (
                    <>
                        <span className={styles.clearButton} onClick={clearAll}>
                            <FaTimes size={12} />
                        </span>
                        <span className={styles.separator} />
                    </>
                )}
                {!isDisabled && (
                    <span className={styles.dropdownIcon}>
                        <FaChevronDown />
                    </span>
                )}
            </div>

            <ReactTooltip id={tooltipId} place="top" type="dark" effect="solid" />

            {isOpen && !isDisabled && (() => {
                const dropdownNode = (
                    <div
                        className={styles.dropdown}
                        ref={portalDropdownRef}
                        style={usePortal ? portalStyle : undefined}
                    >
                        {showMultiOptions && (
                            <div className={styles.multiOptions}>
                                <button
                                    type="button"
                                    className={`${styles.multiOptionButton} ${styles.selectAllButton}`}
                                    onMouseDown={e => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        selectAll();
                                    }}
                                >
                                    {t('common.select_all')}
                                </button>
                                <button
                                    type="button"
                                    className={`${styles.multiOptionButton} ${styles.clearAllButton}`}
                                    onMouseDown={e => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        clearAll(e);
                                    }}
                                >
                                    {t('common.clear_all')}
                                </button>
                            </div>
                        )}

                        {canCreateNew && (
                            <div className={styles.addNewOption} onClick={handleCreateOption}>
                                <AddIcon className={styles.addIcon} />
                                {createOptionText || t('common.add')} &quot;{searchTerm}&quot;
                            </div>
                        )}

                        {filteredOptions.length > 0 ? (
                            filteredOptions.map(option => {
                                const selected = isSelected(option.value);
                                return (
                                    <div
                                        key={option.value}
                                        className={`${styles.option} ${selected ? styles.optionSelected : ''}`}
                                        onClick={() => toggleOption(option)}
                                    >
                                        <span className={`${styles.checkbox} ${selected ? styles.checkboxSelected : ''}`}>
                                            {selected && <FaCheck />}
                                        </span>
                                        <span className={styles.optionLabel}>
                                            {option.label}
                                            {option.fullLabel && <span className={styles.optionDescription}>{option.fullLabel}</span>}
                                        </span>
                                    </div>
                                );
                            })
                        ) : (
                            <div className={styles.noResults}>
                                {isCreatable ? t('common.no_options_type_to_add') : t('common.no_options')}
                            </div>
                        )}
                    </div>
                );
                if (usePortal) {
                    return ReactDOM.createPortal(dropdownNode, document.body);
                }
                // Em growScrollOnOpen o spacer aumenta a altura do .container; um wrapper
                // relativo de altura zero mantém o menu (top: 100%) ancorado ao selectBox
                // em vez de descer com o fundo do container.
                return growScrollOnOpen
                    ? <div style={{ position: 'relative' }}>{dropdownNode}</div>
                    : dropdownNode;
            })()}

            {/* Spacer que empurra o conteúdo abaixo e estende o scroll da página
                enquanto a dropdown está aberta (growScrollOnOpen). */}
            {isOpen && !isDisabled && spacerHeight > 0 && (
                <div aria-hidden style={{ height: spacerHeight }} />
            )}
        </div>
    );
};

export default Select;
